import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTemplate } from '../lib/useTemplate';
import AuthModal from '../components/Layout/AuthModal';
import UpgradeModal from '../components/shared/UpgradeModal';
import usePageMeta from '../lib/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TemplatePreviewPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [user, setUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingResume, setPendingResume] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  usePageMeta(
    preview?.name ? `${preview.name} template` : 'Template',
    preview?.description || (preview?.name ? `Customise the ${preview.name} template in the iVisora editor.` : undefined),
    error ? { noindex: true } : { path: `/template/${templateId}` },
  );

  useEffect(() => {
    const raw =
      localStorage.getItem('ivisora_user') || localStorage.getItem('user');
    if (raw) {
      try { setUser(JSON.parse(raw)); } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/templates/${templateId}/preview`)
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? 'not_found' : 'error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [templateId]);

  const handleAuthSuccess = useCallback((authedUser) => {
    if (authedUser) setUser(authedUser);
    setAuthOpen(false);
    if (pendingResume) {
      const resume = pendingResume;
      setPendingResume(null);
      Promise.resolve().then(() => resume());
    }
  }, [pendingResume]);

  const handleUse = useCallback(() => {
    if (!preview) return;
    useTemplate(preview, {
      navigate,
      user,
      openAuthModal: ({ onSuccess }) => {
        setPendingResume(() => onSuccess);
        setAuthOpen(true);
      },
      openUpgradeModal: ({ feature } = {}) => {
        setUpgradeFeature(feature || null);
        setUpgradeOpen(true);
      },
      toast: ({ title }) => {
        if (typeof window !== 'undefined') window.alert(title);
      },
    });
  }, [preview, navigate, user]);

  if (loading) {
    return <div className="p-12 text-center text-gray-500">Loading…</div>;
  }
  if (error === 'not_found') {
    return (
      <div className="p-12 text-center">
        <p className="text-lg text-gray-700">Template no longer available.</p>
        <p className="text-sm text-gray-500 mt-2">
          This link may have expired or the template was removed.
        </p>
      </div>
    );
  }
  if (!preview) {
    return <div className="p-12 text-center text-gray-700">Something went wrong.</div>;
  }

  const settings = preview.share_settings || {};
  const useTemplateAllowed = settings.allow_use_template !== false;

  return (
    <div className="max-w-3xl mx-auto p-8">
      <div className="bg-gray-50 rounded-2xl overflow-hidden mb-6 border border-gray-200">
        {(preview.thumbnail_url || preview.preview_url) && !thumbnailFailed ? (
          <img
            src={preview.thumbnail_url || preview.preview_url}
            alt={preview.name}
            className="w-full h-auto"
            onError={() => setThumbnailFailed(true)}
          />
        ) : (
          <div
            className="aspect-[4/5] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"
            data-testid="preview-fallback"
          >
            <div className="text-center text-gray-400">
              <div className="text-4xl font-bold">{(preview.name || '?').charAt(0).toUpperCase()}</div>
              <div className="text-xs mt-2">Preview unavailable</div>
            </div>
          </div>
        )}
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">{preview.name}</h1>
      {preview.designer_name && (
        <p className="text-sm text-gray-600 mb-1">By {preview.designer_name}</p>
      )}
      {preview.category && (
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-4">
          {preview.category}
        </p>
      )}
      {preview.is_premium && (
        <p className="text-xs font-medium text-amber-700 bg-amber-50 inline-block px-2 py-0.5 rounded-md mb-4">
          Premium template
        </p>
      )}
      <div className="flex gap-3 mt-6">
        {useTemplateAllowed ? (
          <button
            type="button"
            onClick={handleUse}
            className="bg-blue-600 text-white px-6 py-3 rounded-md font-medium hover:bg-blue-700"
          >
            Use Template
          </button>
        ) : (
          <p className="text-sm text-gray-500">
            The template owner has disabled new uses of this template.
          </p>
        )}
      </div>
      {authOpen && (
        <AuthModal
          open={authOpen}
          onClose={() => setAuthOpen(false)}
          onAuthSuccess={handleAuthSuccess}
        />
      )}
      {upgradeOpen && (
        <UpgradeModal
          onClose={() => setUpgradeOpen(false)}
          feature={upgradeFeature}
        />
      )}
    </div>
  );
}
